'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import CorrectionNote from '@/app/components/blog/CorrectionNote'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does AquaTru Remove Microplastics? Yes: 96.48%, Certified (2026)"
      author="MicroPlastics Research Desk"
      date="July 15, 2026"
      readTime="8 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Does AquaTru remove microplastics: the countertop reverse-osmosis purifier and its certified results"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <CorrectionNote date="September 21, 2026">
        This article previously said AquaTru removes 99%+ of microplastics including nanoplastics, that it is certified to &ldquo;NSF P473&rdquo;, that no NSF standard names microplastics, and that the 2024 PNAS study pointed to reverse osmosis as an effective removal step. AquaTru&apos;s certified microplastics result is 96.48% under NSF/ANSI 401, a standard that does name microplastics; no certification covers nanoplastics; its PFOA/PFOS claim sits under NSF/ANSI 53 and there is no P473 listing; and the PNAS team suggested reverse osmosis in bottling may leave mostly nanoplastics behind.
      </CorrectionNote>

      <BlogTLDR>
        <strong>Yes, and it is certified.</strong> The AquaTru Classic countertop
        reverse-osmosis purifier is certified by IAPMO R&amp;T to NSF/ANSI 401 with
        a microplastics claim: <strong>96.48% average reduction</strong> of
        particles 0.5 to 1 µm. It is also certified to NSF/ANSI 42, 53, 58 and 372,
        which gives it the broadest certified list of any filter we compared,
        including lead, fluoride, arsenic, nitrate and PFOA/PFOS. Two caveats:
        several pitchers post higher certified microplastic figures (PUR PLUS
        99.8%, Brita Elite 99.6%), and no filter, AquaTru included, is certified
        for nanoplastics.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>AquaTru Classic is certified to <strong>NSF/ANSI 401 for microplastics at 96.48%</strong> (particles 0.5–1 µm; the standard requires at least 85%).</>,
          <>It is also certified to NSF/ANSI 42, 53 (PFOA/PFOS and VOCs), 58 (reverse osmosis: lead, fluoride, arsenic, nitrate and more) and 372.</>,
          <>No certification covers nanoplastics, and AquaTru makes no nanoplastic claim.</>,
          <>It needs <strong>no plumbing</strong>: it sits on the counter and you fill a tank. AquaTru rates it 75% efficient.</>,
          <>$475, with filters costing about $110–$130 a year at AquaTru&apos;s prices.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_aquatru" />

      <KeyStats
        heading="AquaTru Classic: the certified numbers"
        stats={[
          { value: '96.48%', label: 'microplastics, 0.5–1 µm', context: 'NSF/ANSI 401, certified by IAPMO R&T', source: 'AquaTru performance data sheet', sourceUrl: 'https://aquatruwater.com/products/countertop-reverse-osmosis-water-purifier' },
          { value: '99.1%', label: 'lead reduction', context: 'certified under NSF/ANSI 58', source: 'AquaTru performance data sheet', sourceUrl: 'https://aquatruwater.com/products/countertop-reverse-osmosis-water-purifier' },
          { value: '93.5%', label: 'fluoride reduction', context: 'certified under NSF/ANSI 58', source: 'AquaTru performance data sheet', sourceUrl: 'https://aquatruwater.com/products/countertop-reverse-osmosis-water-purifier' },
          { value: '75%', label: 'efficiency', context: 'share of water that comes out filtered, per AquaTru', source: 'AquaTru performance data sheet', sourceUrl: 'https://aquatruwater.com/products/countertop-reverse-osmosis-water-purifier' },
        ]}
      />

      <h2>Does AquaTru remove microplastics?</h2>
      <p>
        Yes, with a certified number behind it. The AquaTru Classic (models
        AT2000&ndash;AT2050) is listed by IAPMO R&amp;T under NSF/ANSI 401, and the
        listing includes microplastics. On AquaTru&apos;s performance data sheet the
        microplastics test, on particles 0.5 to 1 µm, shows a{' '}
        <strong>96.48% average reduction</strong> against a required 85%. The
        smaller AquaTru Carafe is certified to the same standards, with a higher
        microplastics result of 98.96%.
      </p>
      <p>
        It is a four-stage system: stages one and two are a combined pre-filter and
        carbon filter, stage three is the reverse-osmosis membrane, and stage four
        is a carbon filter for volatile organic compounds. AquaTru does not publish
        a pore size for the membrane.
      </p>

      <h2>What about nanoplastics?</h2>
      <p>
        No certification tests particles smaller than 0.5 µm, so no filter can show
        certified nanoplastic removal, and AquaTru does not claim it. The 2024{' '}
        <em>PNAS</em> study that counted about 240,000 particles per litre of
        bottled water, most of them nanoplastics, suggested that reverse-osmosis
        membranes in bottling plants may remove larger particles while leaving
        mostly nanoplastics behind. That was industrial bottling, not a home unit,
        but it is a reason not to assume RO catches everything.
      </p>

      <h2>What the certifications cover</h2>
      <ul>
        <li><strong>NSF/ANSI 401</strong>: emerging contaminants, including microplastics (96.48%) and several pharmaceuticals.</li>
        <li><strong>NSF/ANSI 58</strong>: reverse-osmosis performance, including lead (99.1%), fluoride (93.5%), pentavalent arsenic (98.0%), nitrate/nitrite (89.9%), chromium, barium, radium and dissolved solids.</li>
        <li><strong>NSF/ANSI 53</strong>: PFOA/PFOS and volatile organic compounds.</li>
        <li><strong>NSF/ANSI 42</strong>: chlorine, taste and odour. <strong>NSF/ANSI 372</strong>: lead-free materials.</li>
      </ul>
      <p>
        AquaTru reduces pentavalent arsenic, and says it may not remove other forms.
        Reverse osmosis also takes out most dissolved minerals (dissolved solids
        fall by 87.1%), so some owners add mineral drops for taste.
      </p>

      <h2>Cost and maintenance</h2>
      <p>
        The AquaTru Classic costs $475 on AquaTru&apos;s site. The pre/carbon filter
        is changed every 6 months or 600 gallons, the VOC filter every 12 months,
        and the RO membrane every 24 months or 1,200 gallons, which comes to about
        $110&ndash;$130 a year at AquaTru&apos;s prices. It needs a counter and a
        socket, not plumbing.
      </p>

      <h2>AquaTru vs the alternatives</h2>
      <BlogComparisonTable
        caption="Certified microplastic results compared"
        columns={[
          { key: 'filter', label: 'Filter' },
          { key: 'type', label: 'Type' },
          { key: 'mp', label: 'Microplastics (NSF/ANSI 401)' },
          { key: 'also', label: 'Also certified for' },
        ]}
        rows={[
          { filter: 'AquaTru Classic', type: 'Countertop RO', mp: '96.48% (IAPMO)', also: 'Lead, fluoride, arsenic, nitrate, PFOA/PFOS and more' },
          { filter: 'PUR PLUS', type: 'Pitcher', mp: '99.8% (IAPMO)', also: 'Lead, total PFAS' },
          { filter: 'Brita Elite', type: 'Pitcher cartridge', mp: '99.6% (WQA)', also: 'NSF 42 and 53 claims' },
          { filter: 'Aquasana Clean Water Machine', type: 'Powered countertop dispenser', mp: '99.6% (WQA)', also: 'PFOA/PFOS' },
          { filter: 'Clearly Filtered', type: 'Pitcher', mp: 'Not certified (brand lab tests only)', also: 'NSF 42, 372' },
          { filter: 'Berkey', type: 'Gravity-fed', mp: 'Not tested, per Berkey', also: 'None for Black Berkey elements' },
        ]}
      />
      <p>
        Choose AquaTru if you want reverse osmosis for lead, fluoride, arsenic or
        nitrate as well as microplastics. If microplastics are the only goal, a
        certified pitcher costs less and posts a higher certified figure. The full
        comparison is in{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">best water filter for microplastics</Link>;
        see also{' '}
        <Link href="/blog/does-clearly-filtered-remove-microplastics" className="text-forest underline">Clearly Filtered</Link>{' '}
        and{' '}
        <Link href="/blog/does-berkey-filter-microplastics" className="text-forest underline">Berkey</Link>.
      </p>

      <BlogAppCTA
        headline="Filtered at home, but what about everywhere else?"
        sub="AquaTru handles your kitchen tap. Scan the bottled water, to-go cups, and kitchen gear you use away from it with the MicroPlastics app to catch the exposure your filter can't."
      />

      <BlogFAQ
        items={[
          {
            question: 'Is AquaTru certified for microplastics?',
            answer: 'Yes. IAPMO R&T certifies the AquaTru Classic to NSF/ANSI 401 including microplastics, with a 96.48% average reduction of particles 0.5 to 1 µm. It is also certified to NSF/ANSI 42, 53, 58 and 372.',
          },
          {
            question: 'Does AquaTru remove nanoplastics?',
            answer: 'Unknown. No certification tests particles below 0.5 µm and AquaTru makes no nanoplastic claim. The 2024 PNAS bottled-water study suggested reverse osmosis in bottling plants may leave mostly nanoplastics behind.',
          },
          {
            question: 'Does AquaTru remove PFAS and fluoride?',
            answer: 'It is certified for PFOA/PFOS under NSF/ANSI 53 and for fluoride (93.5%) under NSF/ANSI 58, along with lead (99.1%), pentavalent arsenic (98.0%) and nitrate/nitrite (89.9%).',
          },
          {
            question: 'Is AquaTru better than a Berkey for microplastics?',
            answer: 'On the evidence, yes: AquaTru has a certified microplastics result and Berkey says it has not tested its elements for plastic particles. Berkey needs no power; if that matters, certified pitchers such as PUR PLUS and Brita Elite are also gravity-fed.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'AquaTru', year: 2026, title: 'AquaTru Classic performance data sheet (IAPMO R&T: NSF/ANSI 42, 53, 58, 372, 401)', publisher: 'AquaTru', url: 'https://aquatruwater.com/products/countertop-reverse-osmosis-water-purifier' },
          { authors: 'IAPMO R&T', year: 2026, title: 'Certificate W-10195: AquaTru, NSF/ANSI 401 (including microplastics)', publisher: 'IAPMO', url: 'https://myplc.iapmo.org/certificate#/18731' },
          { authors: 'NSF', year: 2026, title: 'Water filter FAQs (NSF/ANSI 401 and microplastics)', publisher: 'NSF', url: 'https://www.nsf.org/about-nsf/faqs/water-filters-faqs' },
          { authors: 'Qian N, Gao X, Lang X, et al.', year: 2024, title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy', publisher: 'Proceedings of the National Academy of Sciences', url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121' },
        ]}
      />
    </BlogPostLayout>
  )
}
